/** 9 culori colorate + transparent (fără griul implicit). */
export const CALENDAR_EVENT_BASE_COLOR_TOKENS = [
   "--red",
   "--orange",
   "--yellow",
   "--green",
   "--blue",
   "--indigo",
   "--purple",
   "--pink",
   "--black-t",
];

/** 10 combinații diagonale 50/50. */
export const CALENDAR_EVENT_COMBO_COLORS = {
   "green-red": ["green", "red"],
   "orange-green": ["orange", "green"],
   "orange-red": ["orange", "red"],
   "violet-red": ["violet", "red"],
   "violet-orange": ["violet", "orange"],
   "violet-green": ["violet", "green"],
   "violet-blue": ["violet", "blue"],
   "violet-pink": ["violet", "pink"],
   "violet-yellow": ["violet", "yellow"],
   "violet-indigo": ["violet", "indigo"],
};

export const CALENDAR_EVENT_COMBO_COLOR_TOKENS = Object.keys(
   CALENDAR_EVENT_COMBO_COLORS,
).map((key) => `--${key}`);

export const CALENDAR_EVENT_DEFAULT_COLOR_TOKEN = "--event-default";

/**
 * Paletă: 10 pe rândul 1 (gri + 8 culori + transparent)
 * + 10 gradiente pe rândul 2.
 */
export const CALENDAR_EVENT_COLOR_TOKENS = [
   CALENDAR_EVENT_DEFAULT_COLOR_TOKEN,
   ...CALENDAR_EVENT_BASE_COLOR_TOKENS,
   ...CALENDAR_EVENT_COMBO_COLOR_TOKENS,
];

export const CALENDAR_EVENT_COLOR_GRID_COLUMNS = 10;

export const CALENDAR_EVENT_COLOR_LABELS = {
   "event-default": "Implicit",
   red: "Roșu",
   orange: "Portocaliu",
   yellow: "Galben",
   green: "Verde",
   blue: "Albastru",
   indigo: "Indigo",
   purple: "Mov",
   pink: "Roz",
   "black-t": "Transparent",
   "green-red": "Verde / Roșu",
   "orange-green": "Portocaliu / Verde",
   "orange-red": "Portocaliu / Roșu",
   "violet-red": "Violet / Roșu",
   "violet-orange": "Violet / Portocaliu",
   "violet-green": "Violet / Verde",
   "violet-blue": "Violet / Albastru",
   "violet-pink": "Violet / Roz",
   "violet-yellow": "Violet / Galben",
   "violet-indigo": "Violet / Indigo",
};

export const CALENDAR_EVENT_COLOR_HINTS = {
   "event-default": "Culoarea implicită din calendar",
   yellow: "Loc liber",
   green: "Achitată",
   red: "Grafic închis",
   orange: "Achitare card în oficiu",
   indigo: "Lecție stabilită de instructor",
   pink: "Grafic pentru Ciocana/Buiucani",
   blue: "Instructor care activează pe Ciocana",
   purple: "Instructor care activează pe Botanica",
   "black-t": "Transparent",
   "green-red": "Jumătate verde, jumătate roșu",
   "orange-green": "Jumătate portocaliu, jumătate verde",
   "orange-red": "Jumătate portocaliu, jumătate roșu",
   "violet-red": "Jumătate violet, jumătate roșu",
   "violet-orange": "Jumătate violet, jumătate portocaliu",
   "violet-green": "Jumătate violet, jumătate verde",
   "violet-blue": "Jumătate violet, jumătate albastru",
   "violet-pink": "Jumătate violet, jumătate roz",
   "violet-yellow": "Jumătate violet, jumătate galben",
   "violet-indigo": "Jumătate violet, jumătate indigo",
};

export const CALENDAR_EVENT_TONE_SET = new Set([
   "red",
   "orange",
   "yellow",
   "green",
   "blue",
   "indigo",
   "violet",
   "purple",
   "pink",
   "black-t",
]);

export function resolveCalendarComboColorKey(value) {
   let key = String(value || "").trim().toLowerCase();
   if (!key) return "";

   if (key.includes("repeating-linear-gradient") || key.includes("linear-gradient")) {
      for (const comboKey of Object.keys(CALENDAR_EVENT_COMBO_COLORS)) {
         const [first, second] = CALENDAR_EVENT_COMBO_COLORS[comboKey];
         if (
            key.includes(`--event-${first}`) &&
            key.includes(`--event-${second}`)
         ) {
            return comboKey;
         }
      }
   }

   if (key.startsWith("var(")) {
      const match = key.match(/--([a-z-]+)/i);
      if (match?.[1]) key = match[1];
   }

   if (key.startsWith("--event-")) key = key.slice("--event-".length);
   else if (key.startsWith("--")) key = key.slice(2);
   if (key.startsWith("event-")) key = key.slice("event-".length);

   return CALENDAR_EVENT_COMBO_COLORS[key] ? key : "";
}

export function buildCalendarComboColorCss(comboKey) {
   const pair = CALENDAR_EVENT_COMBO_COLORS[comboKey];
   if (!pair) return "";

   const [first, second] = pair;
   const c1 = `var(--event-${first})`;
   const c2 = `var(--event-${second})`;
   return `linear-gradient(135deg, ${c1} 0 50%, ${c2} 50% 100%)`;
}

export function normalizeCalendarEventColorToken(
   value,
   fallback = CALENDAR_EVENT_DEFAULT_COLOR_TOKEN,
) {
   const fallbackToken = CALENDAR_EVENT_COLOR_TOKENS.includes(fallback) ||
      fallback === CALENDAR_EVENT_DEFAULT_COLOR_TOKEN
      ? fallback
      : CALENDAR_EVENT_DEFAULT_COLOR_TOKEN;

   const comboKey = resolveCalendarComboColorKey(value);
   if (comboKey) return `--${comboKey}`;

   const raw = String(value ?? "").trim();
   if (!raw) return fallbackToken;

   const lower = raw.toLowerCase();
   if (
      lower.startsWith("#") ||
      lower.startsWith("rgb(") ||
      lower.startsWith("rgba(") ||
      lower.startsWith("hsl(") ||
      lower.startsWith("hsla(") ||
      lower.startsWith("repeating-linear-gradient(") ||
      lower.startsWith("linear-gradient(")
   ) {
      return raw;
   }

   let suffixSource = lower;
   if (suffixSource.startsWith("var(")) {
      const match = suffixSource.match(/--([a-z-]+)/i);
      if (match?.[1]) suffixSource = `--${String(match[1]).toLowerCase()}`;
   }

   if (suffixSource.startsWith("--")) suffixSource = suffixSource.slice(2);
   if (suffixSource.startsWith("event-")) {
      suffixSource = suffixSource.slice("event-".length);
   }
   suffixSource = suffixSource.trim();
   if (!suffixSource) return fallbackToken;

   // Display maps --black-t → var(--black-s). Reverse that alias on create/copy
   // so transparent reservations keep their color after paste.
   if (suffixSource === "black" || suffixSource === "black-s") {
      suffixSource = "black-t";
   }

   const candidates = [`--${suffixSource}`, `--event-${suffixSource}`];
   for (const candidate of candidates) {
      if (CALENDAR_EVENT_COLOR_TOKENS.includes(candidate)) return candidate;
   }

   return fallbackToken;
}

export function calendarEventColorTokenSuffix(value) {
   const token = normalizeCalendarEventColorToken(value);
   if (!String(token).startsWith("--")) return "event-default";
   return token.replace(/^--/, "");
}

export function calendarEventColorToCss(value) {
   const comboKey = resolveCalendarComboColorKey(value);
   if (comboKey) return buildCalendarComboColorCss(comboKey);

   const raw = String(value || "").trim();
   if (!raw) return "";

   const lower = raw.toLowerCase();
   if (
      lower === "--event-default" ||
      lower === "var(--event-default)" ||
      lower === "--default" ||
      lower === "default"
   ) {
      return "";
   }
   if (
      lower.startsWith("#") ||
      lower.startsWith("rgb(") ||
      lower.startsWith("rgba(") ||
      lower.startsWith("hsl(") ||
      lower.startsWith("hsla(") ||
      lower.startsWith("repeating-linear-gradient(") ||
      lower.startsWith("linear-gradient(")
   ) {
      return raw;
   }
   if (lower.startsWith("var(")) return raw;

   if (lower.startsWith("--event-")) return `var(${lower})`;
   if (lower.startsWith("--")) {
      const suffix = lower.slice(2);
      if (suffix === "black-t") return "var(--black-s)";
      return `var(--event-${suffix})`;
   }

   if (lower === "black-t") return "var(--black-s)";
   return `var(--event-${lower})`;
}

export function calendarEventColorSwatchStyle(token) {
   const css = calendarEventColorToCss(token);
   return css ? { background: css } : undefined;
}
